<template>
	<div
		class="border-t border-b sm:border bg-white text-xs sm:text-base text-black p-4 sm:p-8 -m-6"
		:class="{
			'border-red-700':
				hasTriedToCheckout &&
				(missingFields.length > 0 || missingPatterns.length > 0),
			'border-white':
				!hasTriedToCheckout ||
				(missingFields.length == 0 && missingPatterns.length == 0)
		}"
		v-if="showForm"
	>
		<div class="flex justify-between">
			<div class="font-bold text-base sm:text-xl">
				{{ heading }}
			</div>
			<div
				v-if="
					participant.FirstName + participant.LastName == '' &&
						uniqueParticipants.length > 0
				"
				class="flex flex-col text-xs font-normal"
			>
				<button
					type="button"
					class="p-0 bg-transparent m-0 border-0 text-top h-8"
					@click="copyFromParticipant(otherParticipant)"
					v-for="(otherParticipant, participantIndex) in uniqueParticipants"
					:key="participantIndex"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						class="fill-current w-3"
						viewBox="0 0 14 14"
					>
						<path
							d="M6 0h6l2 2v6l-2 2h-2v2l-2 2H2l-2-2V6l2-2h2V2l2-2zM4 6H2v6h6v-2H6L4 8V6zm2-4v6h6V2H6z"
						/>
					</svg>
					Kopiér {{ otherParticipant.FirstName }}
					{{ otherParticipant.LastName }}
				</button>
			</div>
		</div>

		<div class="flex flex-wrap -ml-2 mt-6" v-if="isRegularCheckout">
			<participant-field
				class="w-full md:w-1/2"
				label="Fornavn"
				name="firstName"
				v-model="participant.FirstName"
				:required="true"
				:show-valid="isValid('FirstName')"
				:show-required-message="isMissing('FirstName')"
				@blur="validate('FirstName')"
				@keyup="[saveParticipantData(), validate('FirstName', true)]"
				@change="[saveParticipantData(), validate('FirstName', false)]"
			/>
			<participant-field
				class="w-full mt-6 md:mt-0 md:w-1/2"
				label="Efternavn"
				name="lastName"
				v-model="participant.LastName"
				:required="true"
				:show-valid="isValid('LastName')"
				:show-required-message="isMissing('LastName')"
				@blur="validate('LastName')"
				@keyup="[saveParticipantData(), validate('LastName', true)]"
				@change="[saveParticipantData(), validate('LastName', false)]"
			/>
		</div>

		<div class="flex -ml-2 mt-6" v-if="isRegularCheckout">
			<participant-field
				type="email"
				label="E-mail"
				v-model="participant.Email"
				:required="true"
				:show-valid="isValid('Email') && isPatternValid('Email')"
				:show-required-message="isMissing('Email')"
				:show-pattern-message="!isMissing('Email') && isPatternMissing('Email')"
				pattern-error-message="Indtast venligst i formatet xx@xx.xx"
				@blur="
					[
						saveParticipantData(),
						validate('Email', false),
						validatePattern('Email', false, emailPattern)
					]
				"
				@keyup="
					[
						saveParticipantData(),
						validate('Email', true)
					]
				"
				@change="
					[
						saveParticipantData(),
						validate('Email', false)
					]
				"
			/>
		</div>

		<div class="flex -ml-2 mt-6" v-if="isRegularCheckout">
			<participant-field
				type="tel"
				label="Mobilnummer"
				name="mobile"
				v-model="participant.Mobile"
				:required="true"
				:show-valid="isValid('Mobile') && isPatternValid('Mobile')"
				pattern-error-message="Indtast venligst 8 cifre"
				:show-pattern-message="isPatternMissing('Mobile')"
				:show-required-message="isMissing('Mobile')"
				@blur="
					[
						saveParticipantData(),
						validate('Mobile', false),
						validatePattern('Mobile', false, '\\d{8}')
					]
				"
				@keyup="
					[
						saveParticipantData(),
						validate('Mobile', true)
					]
				"
				@change="
					[
						saveParticipantData(),
						validate('Mobile', false)
					]
				"
			/>
		</div>

		<div class="flex -ml-2 mt-6 flex-wrap" v-if="isRegularCheckout">
			<participant-field
				class="md:w-1/2"
				label="Køn"
				name="gender"
				type="select-string"
				v-model="participant.Gender"
				:values="[
					{ Key: 'Mand', Value: 'Mand' },
					{ Key: 'Kvinde', Value: 'Kvinde' },
					{ Key: 'Andet', Value: 'Andet' }
				]"
				:required="true"
				:show-valid="isValid('Gender')"
				:show-required-message="isMissing('Gender')"
				@blur="validate('Gender')"
				@keyup="[saveParticipantData(), validate('Gender', false)]"
				@change="
					[
						setValue('Gender', $event),
						saveParticipantData(),
						validate('Gender', false)
					]
				"
			/>
			<participant-field
				class="mt-6 md:mt-0 md:w-1/2"
				label="Fødselsdato"
				name="dateOfBirth"
				type="date"
				placeholder="dd/mm/åååå"
				v-model="participant.DateOfBirthString"
				:required="true"
				:show-valid="
					isValid('DateOfBirthString') && isPatternValid('DateOfBirthString')
				"
				:show-required-message="isMissing('DateOfBirthString')"
				@blur="
					[
						saveParticipantData(),
						validate('DateOfBirthString', false),
						validatePattern('DateOfBirthString', false, dobPattern)
					]
				"
				@keyup="
					[
						saveParticipantData(),
						validate('DateOfBirthString', true)
					]
				"
				@change="
					[
						saveParticipantData(),
						validate('DateOfBirthString', true)
					]
				"
				pattern-error-message="Indtast venligst i formatet dd/mm/åååå"
				:show-pattern-message="
					!isMissing('DateOfBirthString') &&
						isPatternMissing('DateOfBirthString')
				"
			/>
		</div>

		<div class="flex -ml-2 mt-6" v-if="isRegularCheckout">
			<participant-field
				v-model="participant.CompanyName"
				name="companyName"
				:required="false"
				@change="[saveParticipantData()]"
				@keyup="[saveParticipantData()]"
			>
				<span class="pb-2 font-bold"
					>Firma <span class="font-normal">(valgfri)</span></span
				>
			</participant-field>
		</div>

		<div class="flex -ml-2 mt-6" v-if="isRegularCheckout">
			<participant-field
				v-model="participant.Address"
				:required="true"
				:show-valid="isValid('Address')"
				:show-required-message="isMissing('Address')"
				@blur="validate('Address')"
				@keyup="[saveParticipantData(), validate('Address', true)]"
				@change="[saveParticipantData(), validate('Address', true)]"
			>
				<span class="pb-2 flex items-center justify-between">
					<span class="font-bold">Adresse</span>
					<button
						type="button"
						class="p-0 bg-transparent m-0 border-0 text-top-text h-8 text-xs"
						@click="copyAddressFromParticipant1()"
						v-if="
							!(
								participant.FirstName + participant.LastName == '' &&
								uniqueParticipants.length > 0
							) &&
								firstParticipant &&
								(index > 0 || olIndex > 0) &&
								(participant.Address == null || participant.Address == '')
						"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							class="fill-current w-3"
							viewBox="0 0 14 14"
						>
							<path
								d="M6 0h6l2 2v6l-2 2h-2v2l-2 2H2l-2-2V6l2-2h2V2l2-2zM4 6H2v6h6v-2H6L4 8V6zm2-4v6h6V2H6z"
							/>
						</svg>
						Kopiér adresse
						<span class="hidden md:inline">(samme husstand)</span>
					</button>
				</span>
			</participant-field>
		</div>

		<div class="flex -ml-2 mt-6" v-if="isRegularCheckout">
			<participant-field
				class="md:w-1/3"
				label="Post nr."
				v-model="participant.Zip"
				:required="true"
				:show-valid="isValid('Zip') && isPatternValid('Zip')"
				:show-required-message="isMissing('Zip')"
				pattern-error-message="Indtast venligst 4 cifre"
				:show-pattern-message="!isMissing('Zip') && isPatternMissing('Zip')"
				@blur="
					[
						saveParticipantData(),
						validate('Zip'),
						validatePattern('Zip', false, zipPattern)
					]
				"
				@keyup="
					[
						saveParticipantData(),
						validate('Zip', true)
					]
				"
				@change="
					[
						saveParticipantData(),
						validate('Zip', true)
					]
				"
			/>
			<participant-field
				class="md:w-2/3"
				label="By"
				name="city"
				v-model="participant.City"
				:required="true"
				:show-valid="isValid('City')"
				:show-required-message="isMissing('City')"
				@blur="validate('City')"
				@keyup="[saveParticipantData(), validate('City', true)]"
				@change="[saveParticipantData(), validate('City', true)]"
			/>
		</div>

		<div
			class="flex -ml-2 mt-6"
			v-for="field in participant.ProductProperties"
			:key="field.Id"
		>
			<participant-field
				:label="field.Name"
				:name="field.Id"
				v-model="field.Value"
				:required="true"
				:pattern="field.Pattern !== '' ? field.Pattern : null"
				:pattern-message="field.PatternMessage"
				:pattern-error-message="field.PatternMessage"
				:show-valid="
					isValid(field.Id) &&
						(field.Pattern == null ||
							field.Pattern == '' ||
							isPatternValid(field.Id))
				"
				:show-required-message="isMissing(field.Id)"
				:show-pattern-message="
					!isMissing(field.Id) && isPatternMissing(field.Id)
				"
				@blur="[validateField(field), validatePattern(field)]"
				@keyup="
					[
						saveParticipantData(),
						validateField(field, true)
					]
				"
				@change="
					[
						saveParticipantData(),
						validateField(field, true)
					]
				"
			/>
		</div>

		<div
			class="flex -ml-2 mt-6"
			v-for="field in participant.CustomFields"
			:key="field.Id"
		>
			<participant-field
				:label="field.Name"
				:name="field.Id"
				v-model="field.Value"
				:required="true"
				:pattern="field.Pattern !== '' ? field.Pattern : null"
				:pattern-message="field.PatternMessage"
				:pattern-error-message="field.PatternMessage"
				:show-valid="
					isValid(field.Id) &&
						(field.Pattern == null ||
							field.Pattern == '' ||
							isPatternValid(field.Id))
				"
				:show-required-message="isMissing(field.Id)"
				:show-pattern-message="
					!isMissing(field.Id) && isPatternMissing(field.Id)
				"
				@blur="[greet, validateField(field), validatePattern(field)]"
				@keyup="
					[
						greet,
						saveParticipantData(),
						validateField(field, true)
					]
				"
				@change="
					[
						greet,
						saveParticipantData(),
						validateField(field, true)
					]
				"
			/>
		</div>

		<div
			class="flex -ml-2 mt-6"
			v-for="field in participant.DropdownProperties"
			:key="field.Id"
		>
			<participant-field
				type="select"
				:label="field.Name"
				:name="field.Id"
				v-model="field.Value"
				:values="field.Values"
				:required="true"
				:show-valid="isValid(field.Id)"
				:show-required-message="isMissing(field.Id)"
				@blur="[greet, validateField(field)]"
				@change="
					[
						setFieldValue(field, $event),
						saveParticipantData(),
						validateField(field)
					]
				"
			/>
		</div>
	</div>
</template>

<script>
import debounce from "lodash/debounce";
import ParticipantField from "./ParticipantField";

export default {
	props: {
		participant: Object,
		index: Number,
		olIndex: Number
	},
	data: function() {
		return {
			validatedFields: [],
			validFields: [],
			validPatterns: [],
			missingFields: [],
			missingPatterns: [],
			zipPattern: /^\d{4}$/g,
			phonePattern: /^\d{8}$/g,
			emailPattern: /^[^@\s]+@[^@\s]+\.[^@\s]+$/g,
			dobPattern: /^(?:[012][0-9]|3[01])\/(?:0[0-9]|1[0-2])\/(?:19|20)[0-9]{2}$/g
		};
	},
	components: {
		ParticipantField
	},
	methods: {
		setValue: function(property, value) {
			this.participant[property] = value;
		},
		setFieldValue: function(property, value) {
			property.Value = value;
		},
		showBirthDayPatternMessage: function() {
			if (!this.participant.DateOfBirthString) {
				return false;
			}
			var matches = this.participant.DateOfBirthString.match();
			return this.participant.DateOfBirthString != "" && matches == null;
		},
		copyFromParticipant: function(participant) {
			this.$store.commit("copyParticipantTo", {
				participant: participant,
				olIndex: this.olIndex,
				index: this.index
			});
		},
		copyAddressFromParticipant1: function() {
			this.$store.commit("copyParticipantTo", {
				participant: this.$store.getters.firstParticipant,
				olIndex: this.olIndex,
				index: this.index,
				onlyAddress: true
			});
		},
		saveParticipantData: function() {
			this.$store.dispatch("saveBasket", this.$store.getters.basket);
		},
		isMissing: function(fieldName) {
			return this.missingFields.indexOf(fieldName) > -1;
		},
		isPatternMissing: function(fieldName) {
			return this.missingPatterns.indexOf(fieldName) > -1;
		},
		isValid: function(fieldName) {
			return (
				this.validatedFields.indexOf(fieldName) > -1 &&
				this.validFields.indexOf(fieldName) > -1
			);
		},
		isPatternValid: function(fieldName) {
			return this.validPatterns.indexOf(fieldName) > -1;
		},
		validate: function(property, ifInvalid) {
			if (!ifInvalid || this.isMissing(property)) {
				this.validatedFields.push(property);
				this.missingFields = this.missingFields.filter(f => f != property);
				this.validFields = this.validFields.filter(f => f != property);
				var valid =
					this.participant[property] && this.participant[property] != "";

				if (!valid) {
					this.missingFields.push(property);
				} else {
					this.validFields.push(property);
				}
				this.reportValidation();
			}
		},
		validateField: function(property, ifInvalid) {
			if (!ifInvalid || this.isMissing(property.Id)) {
				this.validatedFields.push(property.Id);
				this.missingFields = this.missingFields.filter(f => f != property.Id);
				this.validFields = this.validFields.filter(f => f != property.Id);
				var valid = property.Value && property.Value != "";

				if (!valid) {
					this.missingFields.push(property.Id);
				} else {
					this.validFields.push(property.Id);
				}
				this.reportValidation();
			}
		},
		validatePattern: function(property, ifInvalid, pattern) {
			var id = property.Id || property;
			if (!ifInvalid || this.isPatternMissing(id)) {
				pattern = pattern || property.Pattern;
				if (pattern && pattern !== "") {
					this.validatedFields.push(id);
					this.missingPatterns = this.missingPatterns.filter(f => f != id);
					this.validPatterns = this.validPatterns.filter(f => f != id);
					var value = property.Value
						? property.Value
						: this.participant[property] ?? "";

					if (value) {
						var valid = value.match(pattern) != null;
						if (!valid) {
							this.missingPatterns.push(id);
						} else {
							this.validPatterns.push(id);
						}
					}
					this.reportValidation();
				}
			}
		},
		validateAll: function() {
			if (this.hasTriedToCheckout) {
				if (this.isRegularCheckout) {
					this.validate("FirstName");
					this.validate("LastName");
					this.validate("Email");
					this.validatePattern("Email", false, this.emailPattern);
					this.validate("Mobile");
					this.validatePattern("Mobile", false, this.phonePattern);
					this.validate("Gender");
					this.validate("DateOfBirthString");
					this.validatePattern("DateOfBirthString", false, this.dobPattern);
					this.validate("Address");
					this.validate("Zip");
					this.validatePattern("Zip", false, this.zipPattern);
					this.validate("City");
				}
				this.participant.ProductProperties.map(field => {
					this.validateField(field);
					this.validatePattern(field);
				});
				this.participant.CustomFields.map(field => {
					this.validateField(field);
					this.validatePattern(field);
				});
				this.participant.DropdownProperties.map(field => {
					this.validateField(field);
				});
			}
		},
		reportValidation: function() {
			var missing = this.missingFields.concat(this.missingPatterns);
			this.$store.dispatch("reportValidation", {
				olIndex: this.olIndex,
				index: this.index,
				missingFields: [...new Set(missing)].length
			});
		}
	},
	computed: {
		hasTriedToCheckout: function() {
			return this.$store.getters.hasTriedToCheckout;
		},
		uniqueParticipants: function() {
			let participantExists = function(listOfParticipants, participant) {
				return (
					listOfParticipants.filter(
						p =>
							participant.FirstName + participant.LastName ==
							p.FirstName + p.LastName
					).length > 0
				);
			};

			console.log(this.$store.getters.uniqueParticipants);
			console.log(
				this.$store.getters.basket.OrderLines[this.olIndex].Participants
			);

			var participants = this.$store.getters.uniqueParticipants.filter(
				p =>
					!participantExists(
						this.$store.getters.basket.OrderLines[this.olIndex].Participants,
						p
					)
			);
			return participants;
		},
		heading: function() {
			return "Deltager " + (this.index + 1);
		},
		firstParticipant: function() {
			return this.$store.getters.firstParticipant;
		},
		checkoutState: function() {
			return this.$store.getters.checkoutState;
		},
		isMobilePayCheckout: function() {
			return this.checkoutState == "mobilePay";
		},
		isMemberCheckout: function() {
			return this.checkoutState == "member";
		},
		isRegularCheckout: function() {
			return !this.isMobilePayCheckout && !this.isMemberCheckout;
		},
		showForm: function() {
			return (
				this.isRegularCheckout ||
				this.participant.ProductProperties.length > 0 ||
				this.participant.CustomFields.length > 0 ||
				this.participant.DropdownProperties.length > 0
			);
		}
	},
	mounted: function() {
		this.validateAll();
	},
	watch: {
		hasTriedToCheckout: function() {
			this.validateAll();
		}
	}
};
</script>
